import { hasAnswerObservation, checklistAnswerValue } from '@/services/checklist/checklistObservations';
import { authorizedSuccessorObservation, mediaPayloadHash, sameMediaRow, withoutSuccessorObservationIntent } from './ChecklistMediaSuccessionContract';
import { sha512Hex } from './PendingOperationTerminalization';

type Row = Record<string, any>;
export const MEDIA_CONFLICT_SCHEMA = 'checklist-media-terminal-resolution/v1' as const;

export interface ChecklistMediaChosenObject {
  schema: 'checklist-media-server-object/v1';
  bucket: string;
  path: string;
  publicUrl: string;
  contentType: string;
  byteLength: number;
  bytesSha512: string;
}

export interface ChecklistMediaAlternative {
  schema: 'checklist-media-alternative/v1';
  companyId: string;
  checklistId: string;
  fieldId: string;
  physicalFieldId: string | null;
  snapshotFieldId: string | null;
  valueId: string;
  pendingId: string;
  originalAuthor: string;
  resolutionActor: string;
  localCopyUri: string;
  originalLocalUri: string;
  bucket: string;
  path: string;
  preparedPublicUrl: string;
  contentType: string;
  byteLength: number;
  bytesSha512: string;
  chosenRemote: Row;
}

export interface ChecklistMediaResolutionReceipt {
  schema: 'checklist-media-resolution-receipt/v1';
  companyId: string;
  checklistId: string;
  valueId: string;
  pendingId: string;
  actor: string;
  originalAuthor: string;
  originalEvidenceOwner: string;
  originalArchiveId: string;
  originalConflictId: string;
  originalPayloadSha512: string;
  resolution: Row;
}

export interface ChecklistMediaConflictCommit {
  pending: Row;
  value: Row;
  parent: Row;
  archive: Row;
  conflict: Row;
  remote: Row;
  resolution: Row;
  canCommit: () => boolean;
}

export { mediaPayloadHash, sameMediaRow } from './ChecklistMediaSuccessionContract';

export const sameMediaIdentityAndImage = (left: Row, right: Row): boolean =>
  ['id', 'empresa_id', 'checklist_id', 'campo_id', 'snapshot_field_id', 'valor'].every(key =>
    (left[key] ?? null) === (right[key] ?? null));

/** Toda intenção de nota bloqueia a adoção destrutiva, inclusive a de outro autor. */
export function hasPendingMediaObservation(rows: readonly Row[], value: Row, pending: Row, additionalFields: readonly unknown[] = []): boolean {
  const fields = [pending.campo_id, value.campo_id, value.snapshot_field_id, ...additionalFields].filter(Boolean);
  return rows.some(row => {
    if (row.operation !== 'checklist_partial_update' || row.item_id !== pending.item_id) return false;
    try {
      const answers = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
      return !answers || fields.some(field => hasAnswerObservation(answers[field]));
    } catch { return true; }
  });
}

/** Conflito aberto mantém a intenção de nota mesmo quando a operação já saiu da fila. */
export function hasOpenMediaObservation(rows: readonly Row[], value: Row, pending: Row, additionalFields: readonly unknown[] = []): boolean {
  const fields = [pending.campo_id, value.campo_id, value.snapshot_field_id, ...additionalFields].filter(Boolean);
  return rows.some(row => {
    if (row.empresa_id !== pending.empresa_id || row.collection !== 'guincho_valores_checklist' ||
        row.item_id !== pending.item_id || !['open', 'blocked'].includes(row.status)) return false;
    try {
      const payload = typeof row.payload === 'string' ? JSON.parse(row.payload) : row.payload;
      return (payload?.id === value.id || fields.includes(row.field_id) || fields.includes(row.snapshot_field_id)) &&
        Object.prototype.hasOwnProperty.call(payload ?? {}, 'observacao');
    } catch { return fields.includes(row.field_id) || fields.includes(row.snapshot_field_id); }
  });
}

export const hasMediaCancellation = (rows: readonly Row[], pending: Row): boolean => rows.some(row =>
  row.empresa_id === pending.empresa_id && row.item_id === pending.item_id &&
  row.reason === 'checklist-create-delete-cancellation/v1' && row.status !== 'resolved');

/** Valida novamente dentro da transação; nenhuma decisão de rede remove edição posterior. */
export function planChecklistMediaConflictCommit(input: ChecklistMediaConflictCommit, rows: {
  archive?: Row | null; conflict?: Row | null; pending?: Row | null; value?: Row | null; parent?: Row | null;
  archives?: Row[]; images: Row[]; operations: Row[]; tombstones: Row[]; conflicts: Row[];
}): { partials: Array<{ id: string; data: string | null }>; archiveData: string; receipt?: Row } | null {
  if (!input.canCommit() || !sameMediaRow(rows.archive, input.archive) ||
      !sameMediaRow(rows.conflict, input.conflict) || !sameMediaRow(rows.pending, input.pending) ||
      !sameMediaRow(rows.value, input.value) || !sameMediaRow(rows.parent, input.parent)) return null;
  const { pending, value, archive, conflict, remote, resolution } = input;
  const originalPhysicalField = resolution.originalValue?.campo_id;
  let data: Row;
  let payload: Row;
  try { data = JSON.parse(archive.data); payload = JSON.parse(conflict.payload); } catch { return null; }
  const ownNote = authorizedSuccessorObservation(pending, value, payload, rows.archives ?? [], resolution.preparedRemote) &&
    resolution.kind === 'combine' && resolution.payload?.observacao === payload.observacao &&
    remote.observacao === payload.observacao;
  const intents = withoutSuccessorObservationIntent(rows.operations, rows.conflicts, conflict, payload, ownNote,
    [pending.campo_id, value.campo_id, value.snapshot_field_id, originalPhysicalField]);
  const ids = [pending.item_id, pending.campo_id, value.id, value.campo_id, originalPhysicalField, value.snapshot_field_id, input.parent.template_id].filter(Boolean);
  if (input.parent.retired_at || input.parent.empresa_id !== pending.empresa_id ||
      rows.tombstones.some(row => row.empresa_id === pending.empresa_id && ids.includes(row.entity_id)) ||
      rows.operations.some(row => row.operation === 'delete' && ids.includes(row.item_id)) ||
      rows.images.filter(row => row.item_type === 'CHECKLIST' && row.item_id === pending.item_id &&
        ids.includes(row.campo_id)).length !== 1 || hasPendingMediaObservation(intents.operations, value, pending, [originalPhysicalField]) ||
      hasOpenMediaObservation(intents.conflicts, value, pending, [originalPhysicalField]) || hasMediaCancellation(rows.conflicts, pending)) return null;
  if (archive.accounting_state !== 'conflict_materialized' || archive.operation !== 'checklist_partial_update' ||
      archive.collection !== 'guincho_valores_checklist' || archive.item_id !== pending.item_id ||
      archive.empresa_id !== pending.empresa_id || conflict.empresa_id !== pending.empresa_id ||
      conflict.user_id !== archive.user_id || !['open', 'blocked'].includes(conflict.status) ||
      !['optimistic_lock_failed', 'expected_updated_at_mismatch'].includes(conflict.reason) ||
      archive.reason !== conflict.reason || archive.id !== `accounted:${conflict.id.slice('conflict:'.length)}` ||
      archive.payload_sha512 !== conflict.payload_sha512 || mediaPayloadHash(payload) !== archive.payload_sha512 ||
      JSON.stringify(data.payload) !== JSON.stringify(payload) || data.idempotency_key !== payload.idempotency_key ||
      payload.id !== value.id || payload.valor !== pending.prepared_public_url || Object.prototype.hasOwnProperty.call(payload, 'observacao') && !ownNote ||
      !pending.user_id || pending.baseline_captured !== 1 ||
      (value.valor !== pending.local_uri && value.valor !== payload.valor) ||
      remote.id !== value.id || remote.empresa_id !== pending.empresa_id || remote.checklist_id !== pending.item_id ||
      (remote.snapshot_field_id ?? null) !== (value.snapshot_field_id ?? null) ||
      (!value.snapshot_field_id && remote.campo_id !== value.campo_id) ||
      resolution.schema !== MEDIA_CONFLICT_SCHEMA || resolution.pendingId !== pending.id ||
      typeof remote.updated_at !== 'string' || !Number.isFinite(Date.parse(remote.updated_at))) return null;
  if (resolution.kind === 'server_wins') {
    if (Object.prototype.hasOwnProperty.call(payload, 'observacao')) return null;
    const alternative = resolution.alternative as ChecklistMediaAlternative;
    const chosen = resolution.chosenObject as ChecklistMediaChosenObject;
    if (!alternative || alternative.schema !== 'checklist-media-alternative/v1' || !alternative.localCopyUri ||
        alternative.pendingId !== pending.id || alternative.valueId !== value.id ||
        alternative.companyId !== pending.empresa_id || alternative.checklistId !== pending.item_id ||
        alternative.originalAuthor !== pending.user_id || !alternative.byteLength ||
        !/^[a-f0-9]{128}$/.test(alternative.bytesSha512) ||
        !sameMediaIdentityAndImage(alternative.chosenRemote, remote) ||
        chosen?.schema !== 'checklist-media-server-object/v1' || chosen.bucket !== pending.bucket ||
        chosen.publicUrl !== remote.valor || !chosen.byteLength || !/^[a-f0-9]{128}$/.test(chosen.bytesSha512)) return null;
  } else if (resolution.kind !== 'combine' || remote.valor !== pending.prepared_public_url) return null;
  const partials = [];
  for (const operation of rows.operations) {
    if (operation.operation !== 'checklist_partial_update' || operation.item_id !== pending.item_id) continue;
    let answers: Row;
    try { answers = typeof operation.data === 'string' ? JSON.parse(operation.data) : operation.data; } catch { return null; }
    if (!answers) return null;
    const fields = Object.keys(answers).filter(field => ids.includes(field));
    if (!fields.length) continue;
    if (fields.length !== 1 || operation.id !== conflict.pending_operation_id ||
        operation.user_id !== pending.user_id || operation.empresa_id !== pending.empresa_id ||
        operation.collection !== 'guincho_valores_checklist' ||
        checklistAnswerValue(answers[fields[0]]) !== payload.valor) return null;
    const remaining = { ...answers };
    delete remaining[fields[0]];
    partials.push({ id: operation.id, data: Object.keys(remaining).length ? JSON.stringify(remaining) : null });
  }
  let receipt: Row | undefined;
  if (archive.user_id !== resolution.actor) {
    if (!resolution.actor) return null;
    const proof: ChecklistMediaResolutionReceipt = { schema: 'checklist-media-resolution-receipt/v1',
      companyId: pending.empresa_id, checklistId: pending.item_id, valueId: value.id, pendingId: pending.id,
      actor: resolution.actor, originalAuthor: pending.user_id, originalEvidenceOwner: archive.user_id,
      originalArchiveId: archive.id, originalConflictId: conflict.id, originalPayloadSha512: archive.payload_sha512, resolution };
    const payload = JSON.stringify(proof);
    const id = `media-resolution:${sha512Hex(`${archive.id}:${pending.id}:${resolution.actor}`).slice(0, 48)}`;
    const digest = sha512Hex(payload);
    const existing = rows.conflicts.find(row => row.id === id);
    if (existing && (existing.payload_sha512 !== digest || existing.payload !== payload || existing.user_id !== resolution.actor)) return null;
    const now = Date.now();
    receipt = existing ?? { id, empresa_id: pending.empresa_id, user_id: resolution.actor,
      pending_operation_id: conflict.pending_operation_id, collection: 'guincho_valores_checklist', item_id: pending.item_id,
      field_id: value.campo_id ?? null, snapshot_field_id: value.snapshot_field_id ?? null,
      reason: 'checklist-media-resolution-receipt/v1', payload, payload_sha512: digest,
      status: 'resolved', created_at: now, updated_at: now, resolved_at: now };
  }
  return { partials, receipt, archiveData: JSON.stringify({ ...data, media_resolution: resolution }) };
}
